import { CircleCheck, CircleHelp, CircleX, X } from "lucide-react";
import { Link } from "wouter";
import { cn } from "../../lib/utils.ts";
import { AvatarWithFallback } from "./avatar.tsx";
import { Button } from "./button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip.tsx";

export interface ParticipantLike {
  id: string;
  name?: string;
  value?: string;
  /**
   * The provider-reported organizer, set only on received invitations — an
   * outgoing invitation has no organizer of its own to report, since the
   * provider assigns one from the calendar it is written to. So the label
   * below appears for a real sender and never for the user themselves.
   */
  organizer?: boolean;
  /** This participant is the user. Set on outgoing invitations. */
  isSelf?: boolean;
  required?: boolean;
  state?: "attending" | "declined" | "tentative" | "unknown" | null;
  picture?: string;
  contactId?: string;
}

interface ParticipantPillProps {
  participant: ParticipantLike;
  isExternal?: boolean;
  variant?: "default" | "email";
  className?: string;
  showAvatar?: boolean;
  isAssistant?: boolean;
  /** When isAssistant is true, indicates if this is the current user's assistant */
  isOwnAssistant?: boolean;
  /** When isAssistant is true and not own assistant, the owner's name or email */
  assistantOwnerName?: string;
  showParticipantStatus?: boolean;
  canDelete?: boolean;
  onDelete?: () => void;
  /**
   * Suppress the default click-to-open-contact-sidebar behavior. Set when the
   * pill is rendered as the trigger of a menu/popover and the parent owns the
   * click action instead.
   */
  disableContactSidebar?: boolean;
}

const formatParticipantType = (participant: ParticipantLike) => {
  if (participant.organizer) {
    if (!participant.required) {
      return "organizer, optional";
    }
    return "organizer";
  } else if (!participant.required) {
    return "optional";
  }
  return "";
};

const ParticipantIcon = ({ state }: { state?: ParticipantLike["state"] }) => {
  if (state === "attending") {
    return (
      <CircleCheck className="size-4 bg-green-500 text-primary-foreground rounded-full border-none" />
    );
  }
  if (state === "declined") {
    return <CircleX className="size-4 bg-destructive text-destructive-foreground rounded-full" />;
  }
  // Default for unknown, tentative, or null
  return <CircleHelp className="size-4 rounded-full" />;
};

export const ParticipantPill = ({
  participant,
  isExternal = false,
  variant = "default",
  className,
  showAvatar = variant !== "email",
  showParticipantStatus = variant === "default",
  canDelete = false,
  onDelete,
  isAssistant = false,
  isOwnAssistant = false,
  assistantOwnerName,
  disableContactSidebar = false,
}: ParticipantPillProps) => {
  // Format assistant display name with owner context
  const getAssistantDisplayName = () => {
    const baseName = participant.name || "Okthx";
    if (isOwnAssistant) {
      return `${baseName} (yours)`;
    }
    if (assistantOwnerName) {
      return `${baseName} (${assistantOwnerName})`;
    }
    return baseName;
  };

  const displayName = isAssistant
    ? getAssistantDisplayName()
    : participant.name || participant.value || "Unknown";
  const tooltipText = participant.value || participant.name || "Unknown";

  // A real canonical link preserves browser navigation affordances. The
  // surrounding nav boundary decides whether an ordinary click drills into the
  // canvas, opens the sidebar, or reaches the page.
  const contactHref =
    participant.contactId && !canDelete && !disableContactSidebar
      ? `/contacts/${participant.contactId}`
      : null;

  if (variant === "email") {
    const pillContent = (
      <>
        {showAvatar && (
          <AvatarWithFallback
            size="sm"
            person={{
              ...participant,
              name: participant.name || participant.value || "Unknown",
              value: participant.value || participant.name || "Unknown",
            }}
            className={cn(isExternal && "border-amber-100")}
          />
        )}
        <div className="text-xs truncate pr-1">{displayName}</div>
        {canDelete && !isAssistant && (
          // biome-ignore lint/a11y/useSemanticElements: a <button> would nest inside the kickoff pill's PopoverTrigger <Button> (invalid interactive nesting); keyboard access is via tabIndex + onKeyDown.
          <span
            role="button"
            tabIndex={0}
            aria-label={`Remove ${displayName}`}
            // `!pointer-events-auto` is load-bearing, on the span and the icon
            // both. When this pill is the trigger of a popover/menu it renders
            // inside a <Button>, whose base class sets
            // `[&_svg]:pointer-events-none` — without the override the icon never
            // receives the click; it falls through to the trigger and opens the
            // popover instead of deleting (BIG-5305). stopPropagation then keeps
            // the delete from also bubbling up to that trigger, on both the
            // pointer and the keyboard path.
            className="flex items-center !pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              e.stopPropagation();
              onDelete?.();
            }}
          >
            <X aria-hidden="true" className="size-3 cursor-pointer !pointer-events-auto" />
          </span>
        )}
      </>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {contactHref ? (
            <Link
              href={contactHref}
              aria-label={`Open ${displayName}`}
              className={cn(
                "flex h-6 flex-row items-center gap-1 rounded-full border px-1 py-3",
                className,
              )}
            >
              {pillContent}
            </Link>
          ) : (
            <div
              className={cn(
                "flex h-6 flex-row items-center gap-1 rounded-full border px-1 py-3",
                className,
              )}
            >
              {pillContent}
            </div>
          )}
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Default variant
  const content = (
    <div className="flex flex-row gap-3 items-center">
      <div className="flex flex-row items-center gap-2">
        {showAvatar && (
          <AvatarWithFallback
            size="xs"
            person={{
              ...participant,
              name: participant.name || participant.value || "Unknown",
              value: participant.value || participant.name || "Unknown",
            }}
          />
        )}
        <div className={cn("text-sm @md:text-base", contactHref && "link")}>{displayName}</div>
        {showParticipantStatus && (
          <div className="flex flex-row items-center gap-1.5">
            <ParticipantIcon state={participant.state} />
            {formatParticipantType(participant) && (
              <>
                <div className="text-xs leading-5 text-muted-foreground">-</div>
                <div className="text-xs leading-5 text-muted-foreground">
                  {formatParticipantType(participant)}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render via the design-system <Button> when clickable so we get the shared
  // statsig tracking, focus ring, and disabled-state behavior. `variant="blank"`
  // + `size="fit"` strip the default visual chrome (no bg, no padding, no
  // height) so the pill keeps its existing inline `[avatar] [name] [status]`
  // layout. Non-clickable case stays a <div> — there's no action to trigger.
  if (contactHref) {
    return (
      <Button
        asChild
        variant="blank"
        size="fit"
        data-statsig-name="participant-pill-open-contact"
        className={cn("p-0 font-normal text-left", className)}
      >
        <Link href={contactHref} aria-label={`Open ${displayName}`}>
          {content}
        </Link>
      </Button>
    );
  }
  return <div className={className}>{content}</div>;
};
